import { useState, useEffect, useCallback } from "react";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus, Shield, ChevronDown, ChevronUp, Save, X, Loader2, Eye, PenLine, FilePlus2 } from "lucide-react";
import toast from "react-hot-toast";
import { getRolePermissions, createRolePermission, updateRolePermission, getRoles, getMasterDropdownService } from "@/services/masters";
import { FormField, ScreenCombobox } from "@/components/ui";
import MasterModal from "@/components/masters/MasterModal";
import Pagination from "../common/Pagination";

const permSchema = z.object({
  Role:        z.string().min(1, "Role is required"),
  Screen_Name: z.string().min(1, "Screen name is required"),
  Description: z.string().optional(),
});
type PermFormData = z.infer<typeof permSchema>;

interface Permission {
  id: number;
  Role: string;
  Screen_Name: string;
  Description?: string;
  Can_View: boolean;
  Can_Create: boolean;
  Can_Update: boolean;
  Can_Delete: boolean;
}

const PERM_COLS: {
  key: keyof Pick<Permission, "Can_View" | "Can_Create" | "Can_Update" | "Can_Delete">;
  label: string;
  icon: React.ReactNode;
  activeClass: string;
  checkClass: string;
}[] = [
  { key: "Can_View",   label: "View",   icon: <Eye className="w-3.5 h-3.5" />,      activeClass: "bg-brand-100 text-brand-700 border-brand-300",   checkClass: "accent-brand-500"   },
  { key: "Can_Create", label: "Create", icon: <FilePlus2 className="w-3.5 h-3.5" />, activeClass: "bg-green-100 text-green-700 border-green-300", checkClass: "accent-green-600" },
  { key: "Can_Update", label: "Update", icon: <PenLine className="w-3.5 h-3.5" />,   activeClass: "bg-amber-100 text-amber-700 border-amber-300", checkClass: "accent-amber-500"  },
  // { key: "Can_Delete", label: "Delete", icon: <Trash2 className="w-3.5 h-3.5" />,    activeClass: "bg-red-100 text-red-700 border-red-300",      checkClass: "accent-red-600"   },
];

function PermBadge({ active, label, icon }: { active: boolean; label: string; icon: React.ReactNode }) {
  const col = PERM_COLS.find((c) => c.label === label)!;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${
        active ? col.activeClass : "bg-gray-100 text-gray-400 border-gray-200"
      }`}
    >
      {icon}
      {label}
    </span>
  );
}

export default function RolePermissions() {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [roleOptions, setRoleOptions] = useState<string[]>([]);
  const [screenOptions, setScreenOptions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState<string>("all");
  const [collapsedRoles, setCollapsedRoles] = useState<Record<string, boolean>>({});
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Inline edit state
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<Record<string, boolean>>({});
  const [editDescription, setEditDescription] = useState<string>("");
  const [editScreenName, setEditScreenName] = useState<string>("");
  const [savingId, setSavingId] = useState<number | null>(null);

  // Add modal state
  const [showModal, setShowModal] = useState(false);
  const [newPerm, setNewPerm] = useState({
    Can_View: true,
    Can_Create: false,
    Can_Update: false,
    Can_Delete: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const permMethods = useForm<PermFormData>({
    resolver: zodResolver(permSchema),
    defaultValues: { Role: "", Screen_Name: "" },
  });
  const [totalCount,setTotalCount] = useState(0);

  const loadData = useCallback(async (p = 1, role = "all") => {
    setIsLoading(true);
    try {
      const [permRes, masterRes] = await Promise.all([
        getRolePermissions(role !== "all" ? role : undefined, p),
        getMasterDropdownService(),
      ]);
      if (permRes.status === 200 && permRes.data) {
        const d = permRes.data as any;
        const items: Permission[] = (d.results ?? (Array.isArray(d) ? d : [])).map((r: any) => ({
          ...r,
          Description: r.Description ?? r.description ?? "",
        }));
        if (d.roles) {
        const roleItems: any[] = d.roles ?? (Array.isArray(d) ? d : []);
        setRoleOptions(roleItems.map((r: any) => r).filter(Boolean));
      }
        setPermissions(items);
        setTotalPages(d.total_pages ?? 1);
        setTotalCount(d.count ?? 0);
      }

      if (masterRes.status === 200 && masterRes.data) {
        const screens: string[] = (masterRes.data as any).Screens ?? [];
        setScreenOptions(screens);
      }
    } catch {
      toast.error("Error loading permissions");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadData(1, "all"); }, [loadData]);

  const handlePageChange = (p: number) => { setPage(p); loadData(p, selectedRole); };

  const grouped = permissions.reduce((acc, p) => {
    (acc[p.Role] ??= []).push(p);
    return acc;
  }, {} as Record<string, Permission[]>);

  const handleRoleSelect = (role: string) => {
    setSelectedRole(role);
    setPage(1);
    loadData(1, role);
  };

  // ── Inline edit ──────────────────────────────────────────────
  const startEdit = (p: Permission) => {
    setEditingId(p.id);
    setEditScreenName(p.Screen_Name);
    setEditDescription(p.Description ?? "");
    setEditDraft({
      Can_View: p.Can_View,
      Can_Create: p.Can_Create,
      Can_Update: p.Can_Update,
      Can_Delete: p.Can_Delete,
    });
  };

  const cancelEdit = () => { setEditingId(null); setEditDraft({}); setEditDescription(""); setEditScreenName(""); };

  const closeModal = () => {
    setShowModal(false);
    setNewPerm({ Can_View: true, Can_Create: false, Can_Update: false, Can_Delete: false });
    permMethods.reset();
  };

  const saveEdit = async (id: number) => {
    setSavingId(id);
    try {
      const res = await updateRolePermission(id, {
        Screen_Name: editScreenName,
        Can_View:    editDraft.Can_View   ? 1 : 0,
        Can_Create:  editDraft.Can_Create ? 1 : 0,
        Can_Update:  editDraft.Can_Update ? 1 : 0,
        Can_Delete:  editDraft.Can_Delete ? 1 : 0,
        Description: editDescription,
      } as any);
      if (res.status === 200) {
        toast.success("Permission updated");
        setEditingId(null);
        await loadData(page, selectedRole);
      } else {
        toast.error("Failed to update");
      }
    } catch {
      toast.error("Error saving permission");
    } finally {
      setSavingId(null);
    }
  };

  // ── Add new permission ────────────────────────────────────────
  const submitNew = async (formData: PermFormData) => {
    setIsSubmitting(true);
    try {
      const res = await createRolePermission({
        Role:        formData.Role,
        Screen_Name: formData.Screen_Name,
        Description: formData.Description,
        Can_View:   newPerm.Can_View   ? 1 : 0,
        Can_Create: newPerm.Can_Create ? 1 : 0,
        Can_Update: newPerm.Can_Update ? 1 : 0,
        Can_Delete: newPerm.Can_Delete ? 1 : 0,
      });
      if (res.status === 201 || res.status === 200) {
        toast.success("Permission created");
        setShowModal(false);
        setNewPerm({ Can_View: true, Can_Create: false, Can_Update: false, Can_Delete: false });
        permMethods.reset();
        await loadData(1, selectedRole);
        setPage(1);
      } else {
        const msg = (res.errors as any)?.non_field_errors?.[0] ?? "Failed to create";
        toast.error(msg);
      }
    } catch {
      toast.error("Error creating permission");
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleCollapse = (role: string) =>
    setCollapsedRoles((prev) => ({ ...prev, [role]: !prev[role] }));

  return (
    <div className="space-y-6 p-1">
      {/* ── Header ─────────────────────────────────────────── */}
      <div className="bg-gradient-to-r from-brand-500 to-brand-700 px-6 py-5 rounded-xl shadow-lg">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <Shield className="w-5 h-5 text-white/80" />
              <h2 className="text-2xl font-bold text-white">Role Permissions</h2>
            </div>
            <p className="text-white/70 text-sm">Control what each role can view, create, update, or delete</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white font-semibold py-2.5 px-4 rounded-lg transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            <Plus className="w-4 h-4" />
            Add Permission
          </button>
        </div>

        {/* Role filter chips */}
        <div className="flex flex-wrap gap-2 mt-4">
          {["all", ...roleOptions].map((role) => (
            <button
              key={role}
              onClick={() => handleRoleSelect(role)}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                selectedRole === role
                  ? "bg-white text-brand-700 shadow"
                  : "bg-white/20 text-white hover:bg-white/30"
              }`}
            >
              {role === "all" ? "All Roles" : role}
            </button>
          ))}
        </div>
      </div>
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        totalCount={totalCount}
        itemsPerPage={20}
        onPageChange={(p) => {
          handlePageChange(p)
        }}
      />
      {/* ── Content ────────────────────────────────────────── */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Loading permissions…
        </div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Shield className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="font-medium">No permissions found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).map(([role, perms]) => (
            <div key={role} className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
              {/* Role card header */}
              <button
                className="w-full flex items-center justify-between px-5 py-3.5 bg-gradient-to-r from-slate-50 to-indigo-50 hover:from-brand-50 hover:to-brand-100 transition-colors"
                onClick={() => toggleCollapse(role)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-brand-500 flex items-center justify-center shadow-sm">
                    <Shield className="w-4 h-4 text-white" />
                  </div>
                  <div className="text-left">
                    <p className="font-bold text-gray-800 text-sm">{role}</p>
                    <p className="text-xs text-gray-500">{perms.length} screen{perms.length !== 1 ? "s" : ""}</p>
                  </div>
                </div>
                {collapsedRoles[role]
                  ? <ChevronDown className="w-4 h-4 text-gray-400" />
                  : <ChevronUp className="w-4 h-4 text-gray-400" />
                }
              </button>

              {/* Permissions table */}
              {!collapsedRoles[role] && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50/50">
                        <th className="text-left px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide w-48">
                          Screen
                        </th>
                        {PERM_COLS.map((col) => (
                          <th key={col.key} className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            <div className="flex items-center justify-center gap-1">
                              {col.icon}
                              {col.label}
                            </div>
                          </th>
                        ))}
                        <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {perms.map((perm) => {
                        const isEditing = editingId === perm.id;
                        const isSaving  = savingId  === perm.id;
                        return (
                          <tr
                            key={perm.id}
                            className={`transition-colors ${isEditing ? "bg-brand-50/60" : "hover:bg-gray-50/60"}`}
                          >
                            <td className="px-5 py-3">
                              {isEditing ? (
                                <>
                                  <input
                                    list={`screens-${perm.id}`}
                                    value={editScreenName}
                                    onChange={(e) => setEditScreenName(e.target.value)}
                                    placeholder="Screen name…"
                                    className="w-full text-sm font-medium text-gray-800 border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-brand-400 bg-white"
                                  />
                                  <datalist id={`screens-${perm.id}`}>
                                    {screenOptions.map(s => <option key={s} value={s} />)}
                                  </datalist>
                                  <textarea
                                    value={editDescription}
                                    onChange={(e) => setEditDescription(e.target.value)}
                                    rows={2}
                                    placeholder="Description…"
                                    className="mt-1.5 w-full text-xs text-gray-700 border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-brand-400 resize-none bg-white"
                                  />
                                </>
                              ) : (
                                <>
                                  <span className="font-medium text-gray-800">{perm.Screen_Name}</span>
                                  {perm.Description && (
                                    <p className="text-xs text-gray-400 mt-0.5 leading-snug">{perm.Description}</p>
                                  )}
                                </>
                              )}
                            </td>

                            {PERM_COLS.map((col) => (
                              <td key={col.key} className="text-center px-4 py-3">
                                {isEditing ? (
                                  <input
                                    type="checkbox"
                                    checked={!!editDraft[col.key]}
                                    onChange={(e) =>
                                      setEditDraft((d) => ({ ...d, [col.key]: e.target.checked }))
                                    }
                                    className={`w-4 h-4 rounded cursor-pointer ${col.checkClass}`}
                                  />
                                ) : (
                                  <PermBadge active={perm[col.key]} label={col.label} icon={col.icon} />
                                )}
                              </td>
                            ))}

                            <td className="text-center px-4 py-3">
                              {isEditing ? (
                                <div className="flex items-center justify-center gap-2">
                                  <button
                                    onClick={() => saveEdit(perm.id)}
                                    disabled={isSaving}
                                    className="flex items-center gap-1 px-3 py-1.5 bg-brand-500 hover:bg-brand-700 text-white text-xs font-semibold rounded-lg transition-all disabled:opacity-60"
                                  >
                                    {isSaving
                                      ? <Loader2 className="w-3 h-3 animate-spin" />
                                      : <Save className="w-3 h-3" />
                                    }
                                    Save
                                  </button>
                                  <button
                                    onClick={cancelEdit}
                                    disabled={isSaving}
                                    className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-semibold rounded-lg transition-all"
                                  >
                                    <X className="w-3 h-3" />
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => startEdit(perm)}
                                  className="px-3 py-1.5 text-xs font-semibold text-brand-500 bg-brand-50 hover:bg-brand-100 rounded-lg transition-all"
                                >
                                  Edit
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}



      {/* ── Add Permission Modal ─────────────────────────── */}
      <MasterModal
        isOpen={showModal}
        title="Add Permission"
        onClose={closeModal}
        onSubmit={(e) => { e.preventDefault(); permMethods.handleSubmit(submitNew)(); }}
        isLoading={isSubmitting}
      >
        <FormProvider {...permMethods}>
          <div className="space-y-4">
            <FormField
              control={permMethods.control}
              name="Role"
              label="Role"
              isSelect
              options={[{ value: "", label: "Select role…" }, ...roleOptions.map(r => ({ value: r, label: r }))]}
              error={permMethods.formState.errors.Role?.message}
              onClear={() => permMethods.clearErrors("Role")}
              required
            />
            <Controller
              control={permMethods.control}
              name="Screen_Name"
              render={({ field, fieldState: { error } }) => (
                <ScreenCombobox
                  value={field.value}
                  onChange={field.onChange}
                  screens={screenOptions}
                  label="Screen Name"
                  required
                  error={error?.message}
                />
              )}
            />
            <FormField
              control={permMethods.control}
              name="Description"
              label="Description"
              placeholder="Enter description"
              type="textarea"
              error={permMethods.formState.errors.Description?.message}
              onClear={() => permMethods.clearErrors("Description")}
            />
            {/* Permission checkboxes */}
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">
                Permissions
              </label>
              <div className="grid grid-cols-2 gap-3">
                {PERM_COLS.map((col) => (
                  <label
                    key={col.key}
                    className={`flex items-center gap-2.5 p-3 rounded-xl border-2 cursor-pointer transition-all select-none ${
                      newPerm[col.key]
                        ? col.activeClass + " border-current"
                        : "border-gray-100 bg-gray-50 text-gray-400 hover:border-gray-200"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={newPerm[col.key]}
                      onChange={(e) => setNewPerm((p) => ({ ...p, [col.key]: e.target.checked }))}
                      className={`w-4 h-4 rounded ${col.checkClass}`}
                    />
                    <span className="flex items-center gap-1.5 text-sm font-semibold">
                      {col.icon}
                      {col.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </FormProvider>
      </MasterModal>
    </div>
  );
}
